import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Heart, Eye } from "lucide-react";
import type { Product } from "@/data/types";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { cn } from "@/lib/utils";

const ProductCard = ({ product }: { product: Product }) => {
  const { addItem } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const inWishlist = isInWishlist(product.id);

  return (
    <div className="group relative bg-card rounded-xl border border-border/60 overflow-hidden flex flex-col transition-all duration-500 hover:shadow-[0_8px_30px_-8px_hsl(var(--primary)/0.15)] hover:border-primary/20 hover:-translate-y-1">
      {/* Image area */}
      <Link to={`/product/${product.slug}`} className="block relative overflow-hidden bg-muted min-h-[140px] sm:min-h-[200px]">
        {product.image ? (
          <img 
            src={product.image} 
            alt={`Product photo of ${product.name}`}
            loading="lazy"
            decoding="async"
            width={400}
            height={300}
            className="w-full h-[140px] sm:h-[200px] object-cover transition-transform duration-700 ease-out group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-[140px] sm:h-[200px] flex items-center justify-center bg-gradient-to-br from-muted to-secondary">
            <span className="text-3xl sm:text-5xl opacity-50 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500">
              {product.category === "Solvents & Reagents" ? "🧪" :
               product.category === "Amino Acids & Peptides" ? "🔬" :
               product.category === "Chromatography" ? "📈" :
               product.category === "Cell Culture" ? "🧫" :
               product.category === "Lab Equipment" ? "⚗️" :
               product.category === "Research Chemicals" ? "🔬" :
               product.category === "Industrial Chemicals" ? "🏭" :
               product.category === "Pills and Painkillers" ? "💊" :
               product.category === "Liquid Mercury" ? "🪙" :
               product.category === "Cannabis" ? "🌿" : "📊"}
            </span>
          </div>
        )}

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Quick view button */}
        <Link
          to={`/product/${product.slug}`}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 delay-100 bg-background/90 backdrop-blur-sm text-foreground text-[10px] sm:text-xs font-medium px-3 py-1.5 sm:px-4 sm:py-2 rounded-full flex items-center gap-1.5 hover:bg-background"
        >
          <Eye className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          <span className="hidden sm:inline">Quick View</span>
          <span className="sm:hidden">View</span>
        </Link>

        {/* Badges */}
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex gap-1.5">
          {product.isNew && (
            <Badge className="bg-accent text-accent-foreground text-[8px] sm:text-[10px] px-1.5 sm:px-2 shadow-sm backdrop-blur-sm">
              New
            </Badge>
          )}
          {product.isFeatured && (
            <Badge variant="secondary" className="text-[8px] sm:text-[10px] px-1.5 sm:px-2 shadow-sm backdrop-blur-sm bg-secondary/80">
              Featured
            </Badge>
          )}
        </div>
      </Link>

      {/* Wishlist button */}
      <button
        onClick={() => toggleWishlist(product.id, product.name)}
        aria-label={inWishlist ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
        aria-pressed={inWishlist}
        className={cn(
          "absolute top-2 right-2 sm:top-3 sm:right-3 z-10 p-1.5 sm:p-2 rounded-full backdrop-blur-sm transition-all duration-300 shadow-sm",
          inWishlist
            ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
            : "bg-background/70 text-muted-foreground hover:bg-background hover:text-foreground hover:scale-110"
        )}
      >
        <Heart className={cn("h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform duration-300", inWishlist && "fill-current scale-110")} />
      </button>

      {/* Content */}
      <div className="p-3 sm:p-4 flex flex-col flex-1">
        <p className="text-[8px] sm:text-[10px] uppercase tracking-[0.15em] text-accent font-semibold mb-1 sm:mb-1.5 truncate">
          {product.category}
        </p>
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-display font-semibold text-xs sm:text-sm leading-snug mb-1 sm:mb-1.5 group-hover:text-accent transition-colors duration-300 line-clamp-2">
            {product.name}
          </h3>
        </Link>
        {product.cas && (
          <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 sm:mb-1 hidden sm:block font-mono">CAS: {product.cas}</p>
        )}
        {product.purity && (
          <p className="text-[10px] sm:text-xs text-muted-foreground hidden sm:block">
            Purity: <span className="text-success font-medium">{product.purity}</span>
          </p>
        )}

        <div className="mt-auto pt-3 sm:pt-4 flex items-end justify-between gap-1 border-t border-border/40">
          <div>
            <p className="font-display font-bold text-base sm:text-lg tracking-tight">${product.price.toFixed(2)}</p>
            {product.bulkPrice && (
              <p className="text-[9px] sm:text-[10px] text-muted-foreground hidden sm:block">
                Bulk: <span className="text-accent font-medium">${product.bulkPrice.toFixed(2)}</span>
              </p>
            )}
          </div>
          <Button
            size="sm"
            aria-label={`Add ${product.name} to cart`}
            className="h-7 sm:h-9 text-[10px] sm:text-xs px-2.5 sm:px-3.5 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md hover:scale-105 active:scale-95"
            onClick={() => addItem(product, product.packagingOptions[0])}
          >
            <ShoppingCart className="h-3 w-3 sm:h-3.5 sm:w-3.5 sm:mr-1.5" />
            <span className="hidden sm:inline">Add to Cart</span>
          </Button>
        </div>

        {!product.inStock && (
          <p className="text-[10px] sm:text-xs text-destructive mt-1.5 sm:mt-2 font-semibold tracking-wide uppercase">Out of Stock</p>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
